import { useEffect } from 'react'
import {Bell, CheckCheck, ExternalLink, BookOpen, Briefcase, Calendar, AlertCircle, Clock} from 'lucide-react'
import ColaboradorLayout from '@/components/ColaboradorLayout'
import { useNotifications, NOTIF_TYPE_LABELS, NOTIF_TYPE_COLORS } from '@/hooks/useNotifications'
import { useAuth } from '@/hooks/useAuth'

const TYPE_ICONS: Record<string, React.ElementType> = {
  vacante: Briefcase,
  curso: BookOpen,
  evento: Calendar,
  aviso: AlertCircle,
  recordatorio: Clock,
}

function ColaboradorNotificaciones() {
  const { user } = useAuth()
  const { notifications, loading, error, fetchForAudience, markRead, markAllRead } = useNotifications()

  useEffect(() => {
    if (user?.userId) fetchForAudience('colaboradores', user.userId)
  }, [user?.userId])

  const unreadCount = notifications.filter((n) => !(n.readBy ?? []).includes(user?.userId ?? '')).length

  return (
    <ColaboradorLayout
      title="Notificaciones"
      subtitle="Avisos sobre vacantes, cursos, eventos y recordatorios de Beeznz Work"
    >
      <div className="mb-5 flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {unreadCount > 0 ? (
            <span className="font-semibold text-amber-600">{unreadCount} sin leer</span>
          ) : (
            <span className="text-emerald-600 font-semibold">Todo al día</span>
          )}
          {' '}· {notifications.length} total
        </p>
        {unreadCount > 0 ? (
          <button
            onClick={() => user?.userId && markAllRead(user.userId)}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            <CheckCheck size={14} /> Marcar todo como leído
          </button>
        ) : null}
      </div>

      {error ? (
        <p className="rounded-lg bg-red-50 p-4 text-sm text-red-600">{error}</p>
      ) : loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl bg-slate-100" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-200 py-16 text-center">
          <Bell size={32} className="mb-3 text-slate-300" />
          <p className="font-semibold text-slate-600">Sin notificaciones</p>
          <p className="mt-1 text-sm text-slate-400">Cuando Beeznz Work publique avisos, aparecerán aquí.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {notifications.map((n) => {
            const isRead = (n.readBy ?? []).includes(user?.userId ?? '')
            const TypeIcon = TYPE_ICONS[n.type] ?? AlertCircle
            return (
              <div
                key={n._id}
                onClick={() => user?.userId && !isRead && markRead(n, user.userId)}
                className={`flex cursor-pointer items-start gap-4 rounded-xl border p-4 transition hover:shadow-sm ${
                  isRead ? 'border-slate-200 bg-white' : 'border-amber-200 bg-amber-50'
                }`}
              >
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                  isRead ? 'bg-slate-100 text-slate-500' : 'bg-amber-400 text-slate-900'
                }`}>
                  <TypeIcon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="mb-0.5 flex items-center gap-2">
                    <p className={`text-sm font-semibold ${isRead ? 'text-slate-700' : 'text-slate-900'}`}>{n.title}</p>
                    {!isRead ? <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0" /> : null}
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{n.body}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${NOTIF_TYPE_COLORS[n.type]}`}>
                      {NOTIF_TYPE_LABELS[n.type]}
                    </span>
                    <span className="text-xs text-slate-400">{n.createdAt.slice(0, 10)}</span>
                    {n.linkUrl ? (
                      <a
                        href={n.linkUrl}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1 text-xs text-amber-600 hover:underline"
                      >
                        <ExternalLink size={11} /> Ver más
                      </a>
                    ) : null}
                    {isRead ? (
                      <span className="flex items-center gap-1 text-xs text-slate-400"><CheckCheck size={11} /> Leído</span>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </ColaboradorLayout>
  )
}

export default ColaboradorNotificaciones
